<template>
    <el-dialog
        :visible.sync="dialogVisible"
        width="460px"
        center
        class="coin-choice-dialog"
        :show-close="true"
        @update:visible="onVisibleChange"
    >
        <div slot="title" class="coin-choice-dialog-title">
            <i :class="titleIcon"></i>
            <span>{{ dialogTitle }}</span>
        </div>
        <div class="coin-choice-body">
            <div class="coin-choice-info">
                <div class="coin-choice-row">
                    <i class="el-icon-coin"></i>
                    <span>您当前有 <strong class="num-coins">{{ currentCoins }}</strong> 金币</span>
                </div>
                <div v-if="effectiveFeeSummaryLine" class="coin-choice-row">
                    <i class="el-icon-document"></i>
                    <span>{{ effectiveFeeSummaryLine }}</span>
                </div>
                <div v-else class="coin-choice-row">
                    <i class="el-icon-document"></i>
                    <span>本次 {{ charLabel }}<strong class="num-chars">{{ charCount }}</strong> 字，需消耗 <strong class="num-coins">{{ coinsRequired }}</strong> 金币</span>
                </div>
                <p class="coin-choice-rule">（{{ effectiveRuleText }}）</p>
            </div>
            <div class="coin-choice-actions">
                <el-button
                    v-if="showMiniProgramOption && !isFreeMode"
                    type="success"
                    icon="el-icon-mobile-phone"
                    class="choice-btn choice-btn-mini-program"
                    @click="$emit('open-mini-program')"
                >微信小程序免费使用</el-button>
                <el-button
                    v-if="canUsePrimaryAction"
                    type="primary"
                    icon="el-icon-coin"
                    class="choice-btn"
                    @click="$emit('use-coin')"
                >{{ effectivePrimaryButtonText }}</el-button>
                <el-button
                    v-if="!isFreeMode"
                    type="success"
                    icon="el-icon-video-play"
                    class="choice-btn"
                    @click="$emit('watch-video')"
                >扫码看视频赚金币</el-button>
                <div v-if="!isFreeMode" class="pay-method-row">
                    <span class="pay-method-label">不消耗金币，直接付款：</span>
                    <el-button type="primary" size="small" icon="el-icon-chat-dot-round" class="choice-btn-inline" @click="$emit('pay-direct', 'wechat')">微信支付</el-button>
                    <el-button type="warning" size="small" icon="el-icon-wallet" class="choice-btn-inline" @click="$emit('pay-direct', 'alipay')">支付宝</el-button>
                </div>
            </div>
            <p class="coin-choice-note">{{ effectiveChoiceNote }}</p>
        </div>
    </el-dialog>
</template>

<script>
export default {
    name: 'CoinChoiceDialog',
    props: {
        visible: { type: Boolean, default: false },
        dialogTitle: { type: String, default: '选择支付方式' },
        titleIcon: { type: String, default: 'el-icon-document' },
        charCount: { type: Number, default: 0 },
        coinsRequired: { type: Number, default: 0 },
        ruleText: { type: String, default: '每500字1金币，不足500按500算' },
        primaryButtonText: { type: String, default: '抵扣金币' },
        charLabel: { type: String, default: '约 ' },
        showMiniProgramOption: { type: Boolean, default: false },
        /** 非空时替代「本次 X 字」行，用于按次计费等 */
        feeSummaryLine: { type: String, default: '' },
        choiceNote: { type: String, default: '' }
    },
    computed: {
        dialogVisible: {
            get() { return this.visible },
            set(v) { this.$emit('update:visible', v) }
        },
        currentCoins() {
            const user = this.$store.state.user
            return (user && user.coins != null) ? user.coins : 0
        },
        coinChargeEnabled() {
            return this.$store.state.coinChargeEnabled !== false
        },
        isFreeMode() {
            return !this.coinChargeEnabled
        },
        canUsePrimaryAction() {
            return this.isFreeMode || this.currentCoins >= this.coinsRequired
        },
        effectivePrimaryButtonText() {
            return this.isFreeMode ? '立即免费使用' : this.primaryButtonText
        },
        effectiveRuleText() {
            return this.isFreeMode
                ? (this.$store.state.freeModeNotice || '当前为限时免费模式，本次不扣金币')
                : this.ruleText
        },
        effectiveFeeSummaryLine() {
            if (this.isFreeMode) return this.$store.state.freeModeNotice || '当前为限时免费模式，本次不扣金币'
            return this.feeSummaryLine
        },
        effectiveChoiceNote() {
            if (this.isFreeMode) {
                return '说明：当前为限时免费模式，登录后可直接使用，无需金币、扫码或额外支付。'
            }
            if (this.showMiniProgramOption) {
                return this.choiceNote || '说明：可选择微信小程序免费使用、金币抵扣、扫码看视频赚金币或直接微信/支付宝付款。'
            }
            return this.choiceNote || '说明：可选择使用金币抵扣、扫码看视频赚金币或直接微信/支付宝付款。'
        }
    },
    methods: {
        onVisibleChange(v) {
            this.$emit('update:visible', v)
        }
    }
}
</script>

<style lang="less" scoped>
.coin-choice-dialog-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: 600;
    color: #303133;
}
.coin-choice-dialog-title i {
    margin-right: 8px;
    font-size: 22px;
    color: #409eff;
}
.coin-choice-body {
    padding: 16px 0 8px;
}
.coin-choice-info {
    margin-bottom: 16px;
}
.coin-choice-row {
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 8px;
}
.coin-choice-row i {
    color: #909399;
    font-size: 16px;
}
.num-coins {
    color: #e6a23c;
    margin: 0 2px;
}
.num-chars {
    color: #409eff;
    margin: 0 2px;
}
.coin-choice-rule {
    font-size: 12px;
    color: #909399;
    margin: 8px 0 0 24px;
}
.coin-choice-actions {
    display: flex;
    flex-direction: column;
    gap: 10px;
}
.coin-choice-actions .choice-btn {
    display: flex !important;
    justify-content: flex-start !important;
    align-items: center !important;
    width: 100%;
    margin: 0;
    padding-left: 20px !important;
    text-align: left;
}
.coin-choice-actions .choice-btn ::v-deep .el-icon--left {
    margin-right: 10px;
    width: 18px;
    min-width: 18px;
    text-align: center;
    flex-shrink: 0;
}
.coin-choice-actions .choice-btn-mini-program {
    border-color: #67c23a !important;
    background: linear-gradient(135deg, #67c23a 0%, #85ce61 55%, #a6e07a 100%) !important;
    color: #fff !important;
    font-weight: 700;
    box-shadow: 0 10px 22px rgba(103, 194, 58, 0.2);
}
.coin-choice-actions .choice-btn-mini-program:hover,
.coin-choice-actions .choice-btn-mini-program:focus {
    filter: saturate(1.05);
    box-shadow: 0 14px 28px rgba(103, 194, 58, 0.28);
}
.pay-method-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
}
.pay-method-label {
    font-size: 13px;
    color: #606266;
}
.choice-btn-inline {
    margin: 0;
}
.coin-choice-note {
    margin: 16px 0 0 0;
    padding-top: 12px;
    border-top: 1px solid #ebeef5;
    font-size: 12px;
    color: #909399;
    line-height: 1.5;
}

@media (max-width: 768px) {
    .coin-choice-dialog ::v-deep .el-dialog {
        width: 92vw !important;
        margin: 0 auto !important;
        position: absolute !important;
        top: 50% !important;
        left: 50% !important;
        transform: translate(-50%, -50%) !important;
    }
    .coin-choice-dialog ::v-deep .el-dialog__body {
        padding: 12px 16px 20px;
    }
    .coin-choice-dialog-title {
        font-size: 16px;
    }
    .coin-choice-dialog-title i {
        font-size: 18px;
    }
    .coin-choice-body {
        padding: 12px 0 6px;
    }
    .coin-choice-row {
        font-size: 14px;
    }
    .coin-choice-rule {
        font-size: 12px;
        margin-left: 0;
    }
    .coin-choice-actions .choice-btn {
        padding: 12px 16px !important;
        font-size: 14px;
    }
    .pay-method-row {
        flex-direction: column;
        align-items: stretch;
        gap: 10px;
    }
    .pay-method-label {
        font-size: 13px;
    }
    .coin-choice-actions .choice-btn-inline {
        width: 100%;
        margin: 0;
    }
}
</style>

<!-- 暗黑模式适配（非 scoped） -->
<style lang="less">
.dark-mode .coin-choice-dialog .coin-choice-dialog-title { color: var(--text-primary) !important; }
.dark-mode .coin-choice-dialog .coin-choice-row { color: var(--text-regular); }
.dark-mode .coin-choice-dialog .coin-choice-row i { color: var(--text-secondary); }
.dark-mode .coin-choice-dialog .coin-choice-rule { color: var(--text-secondary); }
.dark-mode .coin-choice-dialog .pay-method-label { color: var(--text-regular); }
.dark-mode .coin-choice-dialog .coin-choice-note { color: var(--text-secondary); border-top-color: var(--border-color); }
</style>
